/* eslint-disable max-len */
import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  NgZone,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  generateFiltersID,
  parseProductSearchFilters,
  ProductSearchFilters,
  ProductSearchSortMode,
  PRODUCT_SORTING_BEST_SALES,
  PRODUCT_SORTING_CHEAPEST_FIRST,
  PRODUCT_SORTING_CHEAPEST_LAST,
} from '@app/akita/api/products/models/product-search.filters.model';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { PhoneFilterOptions } from '@app/akita/api/products/models/phone-filter-options.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaRouterService } from '@app/akita/router/state/router.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState, ScreenSizes } from '@app/akita/screen/state/screen.store';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { SearchSortBy } from '@app/shared/models/api/listing/search/search-sort-by.enum';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';

import { ScrollingModule } from '@angular/cdk/scrolling';

import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

import { InViewportModule } from 'ng-in-viewport';
// eslint-disable-next-line max-len
import { ProductBannerComponent } from '@app/products/components/product-banner/product-banner.component';
import { FooterComponent } from '@app/shared/components/new-footer/footer.component';
import { PhoneBrandsComponent } from '@app/phone-brands/phone-brands.component';
import { FeaturedBreadcrumbsComponent } from '@app/featured/components/featured-breadcrumbs/featured-breadcrumbs.component';
import { ProductsCollectionGridComponent } from '@app/products/components/products-collection-grid/products-collection-grid.component';
import { CollectionTitleComponent } from '../components/collection-title/collection-title.component';
import { FeaturedReviewsCardComponent } from '@app/featured/components/featured-reviews-card/featured-reviews-card.component';
import { FeaturedPhoneFiltersSidebarComponent } from '@app/featured/components/featured-phone-filters-sidebar/featured-phone-filters-sidebar.component';
import { FeaturedFiltersToolbarComponent } from '@app/featured/components/featured-filters-toolbar/featured-filters-toolbar.component';
import { HomeHighlightsComponent } from '@app/home/components/home-highlights/home-highlights.component';
import { SortByFilterComponent } from '@app/components/filters/sort-by-filter/sort-by-filter.component';
import { ProductBrandSelectorComponent } from '@app/products/components/product-brand-selector/product-brand-selector.component';
import { ProductFaqComponent } from '../components/product-faq/product-faq.component';
import { MetaService } from '@app/shared/services/meta.service';
import { WindowUtils } from '@app/shared/utils/window.util';
import { fromUrlSlug } from '@app/shared/utils/url.utils';
import { NavigationBarComponent } from '../../../home-new/components/navigation-bar/navigation-bar.component';

export const MAX_PRICE_AMOUNT = 6000;

@Component({
  selector: 'app-collection-page-view',
  templateUrl: './collection-page.template.html',
  styleUrls: ['./collection-page.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    ScrollingModule,
    InViewportModule,
    FooterComponent,
    ProductsCollectionGridComponent,
    FeaturedPhoneFiltersSidebarComponent,
    FeaturedFiltersToolbarComponent,
    ProductBannerComponent,
    HomeHighlightsComponent,
    SortByFilterComponent,
    PhoneBrandsComponent,
    FeaturedBreadcrumbsComponent,
    CollectionTitleComponent,
    FeaturedReviewsCardComponent,
    ProductBrandSelectorComponent,
    ProductFaqComponent,
    NavigationBarComponent,
  ],
})
export class CollectionPageViewComponent implements AfterViewInit, OnInit, OnDestroy {
  public isBrowser: boolean;
  public lastSearch: string;
  public rtlMode$: Observable<boolean>;
  public screenSize$: Observable<ScreenSizes>;
  public lessThan$: Observable<ScreenSizeInfoState>;
  public biggerThan$: Observable<ScreenSizeInfoState>;

  private readonly searchOptionsSubject: BehaviorSubject<ProductSearchFilters>;
  public readonly searchOptions$: Observable<ProductSearchFilters>;
  public readonly hasSearchOptions$: Observable<boolean>;
  public readonly searchPath$: Observable<string>;

  private readonly loadingSubject: BehaviorSubject<boolean>;
  public readonly loading$: Observable<boolean>;

  private readonly phoneFiltersSubject: BehaviorSubject<PhoneFilterOptions>;
  public readonly phoneFilters$: Observable<PhoneFilterOptions>;

  public featured$: Observable<Product | null | undefined>;
  public search$: Observable<Array<Product>>;
  public isSearching$: Observable<boolean>;
  public hasNextPage$: Observable<boolean>;
  public errorSearching$: Observable<ApiError | null>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaRouterService: AkitaRouterService,
    private readonly translateService: TranslocoService,
    private readonly meta: MetaService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.lastSearch = '';

    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.rtlMode$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.screenSize$ = this.akitaScreenQuery.selectSize();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.loadingSubject = new BehaviorSubject(true as any);
    this.loading$ = this.loadingSubject.asObservable();

    this.phoneFiltersSubject = new BehaviorSubject({} as any);
    this.phoneFilters$ = this.phoneFiltersSubject.asObservable();

    this.searchOptionsSubject = new BehaviorSubject(new ProductSearchFilters());
    this.searchOptions$ = this.searchOptionsSubject.asObservable();
    this.searchPath$ = this.searchOptions$.pipe(
      map((options: ProductSearchFilters) => generateFiltersID(options)),
      distinctUntilChanged()
    );
    this.hasSearchOptions$ = this.searchOptions$.pipe(
      map(this.checkForSearchOptions.bind(this)),
      distinctUntilChanged()
    );

    this.search$ = this.akitaProductsQuery.selectSearch();
    this.isSearching$ = this.akitaProductsQuery.selectIsSearching();
    this.hasNextPage$ = this.akitaProductsQuery.selectSearchHasNextPage();
    this.errorSearching$ = this.akitaProductsQuery.selectErrorSearching();
    this.featured$ = this.search$.pipe(
      map((products: Array<Product>) =>
        (products || []).sort((a, b) => (a?.price?.amount || 0) - (b?.price?.amount || 0))
      ),
      map((sortedProducts: Array<Product>) => (sortedProducts || [])[0] || null)
    );

    this.subscriptions.add(
      this.translateService.langChanges$.pipe(takeUntil(this.destroySubject)).subscribe({
        next: () => {
          const description = this.translateService.translate(
            'title_delivery_status_processing'
          );
          this.meta.setTitle('app_title', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);
          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      })
    );

    this.monitorSearchOptions();
    this.monitorQueryParams();
    this.monitorCountry();
  }

  public ngAfterViewInit(): void {
    this.search(this.searchOptionsSubject.value);
  }

  public ngOnInit(): void {
    this.proccessFilters(this.akitaRouterQuery.paramsAndQueryParams);
  }

  private monitorQueryParams(): void {
    this.subscriptions.add(
      this.akitaRouterQuery
        .selectParamsAndQueryParams()
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (queryParams: Params) => {
            this.proccessFilters(queryParams);
          },
        })
    );
  }

  private monitorCountry(): void {
    this.subscriptions.add(
      this.akitaRouterQuery
        .selectCountry()
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (country: string) => {
            if (country !== this.searchOptionsSubject.value.country) {
              this.proccessFilters(this.akitaRouterQuery.paramsAndQueryParams);
            }
          },
        })
    );
  }

  public onLoadNextPage(): boolean {
    const options = parseProductSearchFilters(this.searchOptionsSubject.value);
    const path = generateFiltersID(options);
    const nextPage = this.akitaProductsQuery.getSearchPageCount(path);
    options.page = nextPage;
    this.searchOptionsSubject.next(options);
    this.search(options);
    return false;
  }

  private search(options?: ProductSearchFilters | null): void {
    const currentSearch = JSON.stringify(options || {});
    if (currentSearch !== this.lastSearch) {
      this.lastSearch = currentSearch;

      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(this.akitaProductsService.searchAsync(options));
        this.subscriptions.add(
          this.akitaProductsService.getAvailableSearchOptionsAsync(options)
        );
      });

      this.loadingSubject.next(false);
    }
  }

  private monitorSearchOptions(): void {
    this.subscriptions.add(
      this.searchPath$.pipe(takeUntil(this.destroySubject)).subscribe({
        next: (path?: string | null) => {
          this.zone.run(() => {
            this.search$ = this.akitaProductsQuery.selectSearch(path);
            this.featured$ = this.search$.pipe(
              map((products: Array<Product>) =>
                (products || [])
                  .sort((a, b) => (a?.price?.amount || 0) - (b?.price?.amount || 0))
                  .find((product) => product.inStock || false)
              )
            );
            this.isSearching$ = this.akitaProductsQuery.selectIsSearching(path);
            this.hasNextPage$ = this.akitaProductsQuery.selectSearchHasNextPage(path);
            this.errorSearching$ = this.akitaProductsQuery.selectErrorSearching(path);
            this.changeDetector.markForCheck();
          });
        },
      })
    );
  }

  public onPhoneFiltersChanged($event?: PhoneFilterOptions | null): void {
    if ($event) {
      const phoneFilters = {
        ...(this.phoneFiltersSubject.value || {}),
        ...($event || {}),
      };

      this.applyFilters({
        sort_by: phoneFilters.sortBy || undefined,
        models: (phoneFilters.models || []).join('|') || undefined,
        series: (phoneFilters.series || []).join('|') || undefined,
        brands: (phoneFilters.brands || []).join('|') || undefined,
        colors: (phoneFilters.colors || []).join('|') || undefined,
        storages: (phoneFilters.storages || []).join('|') || undefined,
        category: (phoneFilters.category || []).join('|') || undefined,
        cpu: (phoneFilters.cpu || []).join('|') || undefined,
        graphics: (phoneFilters.graphics || []).join('|') || undefined,
        launch_date: (phoneFilters.launch_date || []).join('|') || undefined,
        ram: (phoneFilters.ram || []).join('|') || undefined,
        screen_size: (phoneFilters.screen_size || []).join('|') || undefined,
        storage_type: (phoneFilters.storage_type || []).join('|') || undefined,

        min_price: phoneFilters.minPrice || [] || undefined,
        max_price: phoneFilters.maxPrice || [] || undefined,
      });
    }
  }

  public onFilterPhoneSortByChanged($event?: SearchSortBy | null): void {
    if ($event) {
      let sortBy: string | undefined;
      if ($event) {
        sortBy = `${$event || ''}`.toLowerCase();
      }

      this.applyFilters({
        sort_by: sortBy || undefined,
      });
    }
  }

  public onFilterPhoneClearAll($event?: boolean | null): void {
    if ($event) {
      this.applyFilters({
        sort_by: undefined,
        models: undefined,
        series: undefined,
        colors: undefined,
        storages: undefined,
        min_price: undefined,
        max_price: undefined,
        cpu: undefined,
        graphics: undefined,
        launch_date: undefined,
        ram: undefined,
        screen_size: undefined,
        storage_type: undefined,
        text: undefined,
      });
    }
  }

  public onFilterPhoneModelsChanged($event?: Array<string> | null): void {
    if ($event) {
      let models: string | undefined;
      if ($event) {
        models = [...($event || [])].join('|');
      }

      this.applyFilters({
        models: models || undefined,
      });
    }
  }

  public onFilterProductBrandChanged($event?: Array<string> | null): void {
    if ($event) {
      let brands: string | undefined;
      if ($event) {
        brands = [...($event || [])].join('|');
      }

      this.applyFilters({
        brands: brands || undefined,
      });
    }
  }

  public onFilterProductSeriesChanged($event?: Array<string> | null): void {
    if ($event) {
      let series: string | undefined;
      if ($event) {
        series = [...($event || [])].join('|');
      }

      this.applyFilters({
        series: series || undefined,
      });
    }
  }

  public onFilterProductCpuChanged($event?: Array<string> | null): void {
    if ($event) {
      let cpus: string | undefined;
      if ($event) {
        cpus = [...($event || [])].join('|');
      }

      this.applyFilters({
        cpu: cpus || undefined,
      });
    }
  }

  public onFilterProductGraphicsChanged($event?: Array<string> | null): void {
    if ($event) {
      let graphics: string | undefined;
      if ($event) {
        graphics = [...($event || [])].join('|');
      }

      this.applyFilters({
        graphics: graphics || undefined,
      });
    }
  }

  public onFilterProductLaunchDateChanged($event?: Array<number> | null): void {
    if ($event) {
      let params: string | undefined;
      if ($event) {
        params = [...($event || [])].join('|');
      }

      this.applyFilters({
        launch_date: params || undefined,
      });
    }
  }

  public onFilterProductRamChanged($event?: Array<string> | null): void {
    if ($event) {
      let params: string | undefined;
      if ($event) {
        params = [...($event || [])].join('|');
      }

      this.applyFilters({
        ram: params || undefined,
      });
    }
  }

  public onFilterProductScreenSizeChanged($event?: Array<string> | null): void {
    if ($event) {
      let params: string | undefined;
      if ($event) {
        params = [...($event || [])].join('|');
      }

      this.applyFilters({
        screen_size: params || undefined,
      });
    }
  }

  public onFilterProductStorageTypeChanged($event?: Array<string> | null): void {
    if ($event) {
      let params: string | undefined;
      if ($event) {
        params = [...($event || [])].join('|');
      }

      this.applyFilters({
        storage_type: params || undefined,
      });
    }
  }

  public onFilterPhoneColorsChanged($event?: Array<string> | null): void {
    if ($event) {
      let colors: string | undefined;
      if ($event) {
        colors = [...($event || [])].join('|').replace(/\s/g, '+');
      }

      this.applyFilters({
        colors: colors || undefined,
      });
    }
  }

  public onFilterCategoryChanged($event?: Array<string> | null): void {
    if ($event) {
      let category: string | undefined;
      if ($event) {
        category = [...($event || [])].join('|').replace(/\s/g, '+');
      }

      this.applyFilters({
        category: category || undefined,
        brands: undefined, // reset filters on category change
        series: undefined,
        sort_by: undefined,
        models: undefined,
        colors: undefined,
        storages: undefined,
        min_price: undefined,
        max_price: undefined,
        cpu: undefined,
        graphics: undefined,
        launch_date: undefined,
        ram: undefined,
        screen_size: undefined,
        storage_type: undefined,
        text: undefined,
      });
    }
  }

  public onFilterBrandChanged($event?: Array<string> | null): void {
    if ($event) {
      let brand: string | undefined;
      if ($event) {
        brand = [...($event || [])].join('|').replace(/\s/g, '+');
      }

      this.applyFilters({
        brands: brand || undefined,
      });
    }
  }

  public onFilterPhoneStoragesChanged($event?: Array<number> | null): void {
    if ($event) {
      let storages: string | undefined;
      if ($event) {
        storages = [...($event || [])].join('|').replace(/\s/g, '+');
      }

      this.applyFilters({
        storages: storages || undefined,
      });
    }
  }

  public onFilterPhonePriceRangeChanged(
    $event?: {
      minPrice: number;
      maxPrice: number;
    } | null
  ): void {
    if ($event) {
      let minPrice: number | undefined;
      if ($event.minPrice && $event.minPrice > 0 && $event.minPrice < MAX_PRICE_AMOUNT) {
        minPrice = $event.minPrice;
      }

      let maxPrice: number | undefined;

      if ($event.maxPrice && $event.maxPrice < MAX_PRICE_AMOUNT) {
        maxPrice = $event.maxPrice;
      }

      this.applyFilters({
        min_price: minPrice || undefined,
        max_price: maxPrice || undefined,
      });
    }
  }

  private applyFilters(params: Params): void {
    this.akitaRouterService.navigate([], params, {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      preserveFragment: true,
    });
  }

  private proccessFilters(queryParams: Params): void {
    let modelsQuery = queryParams.models || null;
    let brandsQuery = queryParams.brands || null;
    let categoryQuery = queryParams.category || null;
    // filters on url fragments section and key
    switch (queryParams.section) {
      case 'category':
        categoryQuery = queryParams.key.toUpperCase();
        break;
      case 'brand':
        brandsQuery = queryParams.key.charAt(0).toUpperCase() + queryParams.key.slice(1);
        break;
      case 'model':
        modelsQuery = queryParams.key;
        break;
      default:
        break;
    }

    const seriesQuery = queryParams.series || null;
    const colorsQuery = queryParams.colors || null;
    const storagesQuery = queryParams.storages || null;
    const textQuery = queryParams.text || null;
    const cpuQuery = queryParams.cpu || null;
    const graphicsQuery = queryParams.graphics || null;
    const launchDateQuery = queryParams.launch_date || null;
    const ramQuery = queryParams.ram || null;
    const screenSizeQuery = queryParams.screen_size || null;
    const storageTypeQuery = queryParams.storage_type || null;

    const sortByQuery = queryParams.sort_by || null;
    const minPriceQuery = queryParams.min_price || null;
    const maxPriceQuery =
      queryParams.max_price !== MAX_PRICE_AMOUNT ? queryParams.max_price : null;

    const phoneFilters = { ...(this.phoneFiltersSubject.value || {}) };

    let productSort: ProductSearchSortMode | null = null;
    let productStorages: Array<string> | null = null;
    let productDates: Array<string> | null = null;

    if (modelsQuery) {
      const models = decodeURI(fromUrlSlug(modelsQuery)).split('|');
      console.log('model', models);
      phoneFilters.models = models;
    } else {
      phoneFilters.models = [];
    }

    if (brandsQuery) {
      const brands = decodeURI(brandsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.brands = brands;
    } else {
      phoneFilters.brands = [];
    }

    if (seriesQuery) {
      const series = decodeURI(seriesQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.series = series;
    } else {
      phoneFilters.series = [];
    }

    if (categoryQuery) {
      const categories = decodeURI(categoryQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.category = categories;
    } else {
      phoneFilters.category = [];
    }

    if (colorsQuery) {
      const colors = decodeURI(colorsQuery).replace(/[+_]/g, ' ').split('|');
      phoneFilters.colors = colors;
    } else {
      phoneFilters.colors = [];
    }

    if (cpuQuery) {
      const cpus = decodeURI(cpuQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.cpu = cpus;
    } else {
      phoneFilters.cpu = [];
    }

    if (graphicsQuery) {
      const graphics = decodeURI(graphicsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.graphics = graphics;
    } else {
      phoneFilters.graphics = [];
    }

    if (storagesQuery) {
      const storages = decodeURI(storagesQuery).replace(/\+/g, ' ').split('|');

      const parsedStorages: Array<number> = new Array(0);
      productStorages = new Array(0);
      for (const storage of storages) {
        parsedStorages.push(parseInt(storage, 10));
        productStorages.push(storage);
      }

      phoneFilters.storages = parsedStorages;
    } else {
      phoneFilters.storages = [];
    }

    if (launchDateQuery) {
      const launchDate = decodeURI(launchDateQuery).replace(/\+/g, ' ').split('|');

      const parsedDates: Array<number> = new Array(0);
      productDates = new Array(0);
      for (const date of launchDate) {
        parsedDates.push(parseInt(date, 10));
        productDates.push(date);
      }

      phoneFilters.launch_date = parsedDates;
    } else {
      phoneFilters.launch_date = [];
    }

    if (ramQuery) {
      const ram = decodeURI(ramQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.ram = ram;
    } else {
      phoneFilters.ram = [];
    }

    if (screenSizeQuery) {
      const screenSize = decodeURI(screenSizeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.screen_size = screenSize;
    } else {
      phoneFilters.screen_size = [];
    }

    if (storageTypeQuery) {
      const storageType = decodeURI(storageTypeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.storage_type = storageType;
    } else {
      phoneFilters.storage_type = [];
    }

    if (sortByQuery) {
      const sortByNormalized = `${sortByQuery || ''}`.toUpperCase();
      if (sortByNormalized === PRODUCT_SORTING_BEST_SALES) {
        phoneFilters.sortBy = SearchSortBy.NOT_SET;
        productSort = PRODUCT_SORTING_BEST_SALES;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_FIRST ||
        sortByNormalized === SearchSortBy.PRICE_ASC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_ASC;
        productSort = PRODUCT_SORTING_CHEAPEST_FIRST;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_LAST ||
        sortByNormalized === SearchSortBy.PRICE_DESC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_DESC;
        productSort = PRODUCT_SORTING_CHEAPEST_LAST;
      }
    } else {
      phoneFilters.sortBy = null;
    }

    if (minPriceQuery) {
      phoneFilters.minPrice = parseInt(`${minPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.minPrice = undefined;
    }

    if (maxPriceQuery) {
      phoneFilters.maxPrice = parseInt(`${maxPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.maxPrice = undefined;
    }

    if (textQuery) {
      phoneFilters.text = textQuery || undefined;
    } else {
      phoneFilters.text = undefined;
    }

    this.phoneFiltersSubject.next(phoneFilters);
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    /* if (phoneFilters.maxPrice === 6000) {
      phoneFilters.maxPrice = undefined;
    } */

    const searchOptions = parseProductSearchFilters(this.searchOptionsSubject.value);
    searchOptions.model = phoneFilters.models;
    searchOptions.brand = phoneFilters.brands;
    searchOptions.series = phoneFilters.series;
    searchOptions.category = phoneFilters.category;
    searchOptions.color = phoneFilters.colors;
    searchOptions.storage = productStorages;
    searchOptions.sorting = productSort;
    searchOptions.price_min = phoneFilters.minPrice;
    searchOptions.price_max = phoneFilters.maxPrice;
    searchOptions.text = phoneFilters.text;
    searchOptions.cpu = phoneFilters.cpu;
    searchOptions.graphics = phoneFilters.graphics;
    searchOptions.launch_date = phoneFilters.launch_date;
    searchOptions.ram = phoneFilters.ram;
    searchOptions.screen_size = phoneFilters.screen_size;
    searchOptions.storage_type = phoneFilters.storage_type;

    const language = `${this.translateService.getActiveLang() || ''}`.toLowerCase();
    if (language) {
      searchOptions.language = language;
    }

    const country = `${this.akitaRouterQuery.country || ''}`.toUpperCase();
    if (country) {
      searchOptions.country = country;
    }

    const validLocale = this.akitaProductsQuery.validateLocale(language, country);
    searchOptions.language = validLocale.language;
    searchOptions.country = validLocale.country;

    this.searchOptionsSubject.next(searchOptions);
    this.search(searchOptions);
  }

  public trackByProduct(index: number, value?: Product | null): string {
    return `${value?.id || index}`;
  }

  public checkForSearchOptions(): boolean {
    const filters = this.searchOptionsSubject.value;
    return filters.color?.length ||
      filters.model?.length ||
      filters.series?.length ||
      filters.is_featured ||
      filters.page ||
      filters.price_max ||
      filters.price_min ||
      filters.storage ||
      filters.text ||
      filters.cpu?.length ||
      filters.graphics?.length ||
      filters.launch_date?.length ||
      filters.ram?.length ||
      filters.screen_size?.length ||
      filters.storage_type?.length ||
      filters.sorting
      ? true
      : false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
